import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { changePasswordTitle } from "../../Constant/title";
import { useFormik } from "formik";
import { PlaceHolderFormat } from "../../Constant/requireMessage";
import {
  changePassword,
  ConfirmPassword,
  currentPassword,
  Loading,
  newPassword,
  Submit,
} from "../../Constant";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseInput from "../../BaseComponents/BaseInput";
import { changePasswordApi } from "../../Api/AuthApi";
import { checkStatusCodeSuccess, errHandler } from "../../Constant/common";
import { passwordRegex, validationMessages } from "../../Constant/validation";
import * as yup from "yup";
import { toast } from "react-toastify";
import { Lock, CheckCircle, Shield, AlertCircle } from "react-feather";

/* ── Password strength ─────────────────────────────────── */
const getStrength = (pw = "") => {
  if (!pw) return { score: 0, label: "", color: "transparent", width: "0%" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[!@#$%^&*()_\-+={}[\]:;,.?/\\|~]/.test(pw)) score++;
  const map = [
    { label: "Too short", color: "#f43f5e", width: "15%" },
    { label: "Weak", color: "#f43f5e", width: "30%" },
    { label: "Fair", color: "#f59e0b", width: "55%" },
    { label: "Good", color: "#06b6d4", width: "75%" },
    { label: "Strong", color: "#10b981", width: "100%" },
  ];
  return { score, ...map[score] };
};

const StrengthBar = ({ password }) => {
  const s = getStrength(password);
  if (!password) return null;
  return (
    <div style={{ marginTop: -4, marginBottom: 12 }}>
      <div
        style={{
          height: 4,
          borderRadius: 99,
          background: "var(--glass-border)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            borderRadius: 99,
            width: s.width,
            background: s.color,
            transition: "width 0.4s cubic-bezier(0.34,1.56,0.64,1), background 0.3s",
          }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        <span style={{ fontSize: "0.6875rem", color: s.color, fontWeight: 600 }}>{s.label}</span>
        <div style={{ display: "flex", gap: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                width: 18,
                height: 4,
                borderRadius: 99,
                background: s.score >= i ? s.color : "var(--glass-border)",
                transition: "background 0.3s",
              }}
            />
          ))}
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 10px", marginTop: 6 }}>
        {[
          { label: "8+ chars", ok: password.length >= 8 },
          { label: "Uppercase", ok: /[A-Z]/.test(password) },
          { label: "Lowercase", ok: /[a-z]/.test(password) },
          { label: "Number", ok: /[0-9]/.test(password) },
          {
            label: "Special char",
            ok: /[!@#$%^&*()_\-+={}[\]:;,.?/\\|~]/.test(password),
          },
        ].map(({ label, ok }) => (
          <span
            key={label}
            style={{
              fontSize: "0.6875rem",
              fontWeight: 500,
              color: ok ? "#10b981" : "var(--text-muted)",
              display: "flex",
              alignItems: "center",
              gap: 3,
              transition: "color 0.2s",
            }}
          >
            <span>{ok ? "✓" : "○"}</span> {label}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ── Security tips ─────────────────────────────────────── */
const tips = [
  "Use at least 8 characters",
  "Mix uppercase, lowercase, numbers & symbols",
  "Avoid reusing old passwords",
  "Never share your password",
];

const ChangePassword = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    document.title = changePasswordTitle;
  }, []);

  const formValidation = yup.object({
    current_password: yup.string().required(validationMessages.required(currentPassword)),
    new_password: yup
      .string()
      .required(validationMessages.required(newPassword))
      .min(8, validationMessages.passwordLength(newPassword, 8))
      .matches(passwordRegex, validationMessages.passwordComplexity(newPassword)),
    confirm_password: yup
      .string()
      .oneOf([yup.ref("new_password"), null], validationMessages.passwordsMatch(ConfirmPassword))
      .required(validationMessages.required(ConfirmPassword)),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { current_password: null, new_password: null, confirm_password: null },
    validationSchema: formValidation,
    onSubmit: (values) => {
      setLoader(true);
      changePasswordApi({
        currentPassword: values.current_password,
        newPassword: values.new_password,
        confirmPassword: values.confirm_password,
      })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp?.message);
            navigate(-1);
          } else {
            toast.error(resp?.message);
          }
        })
        .catch(errHandler)
        .finally(() => setLoader(false));
    },
  });

  const newPw = formik.values.new_password || "";
  const confirmPw = formik.values.confirm_password || "";
  const passwordsMatch = newPw && confirmPw && newPw === confirmPw;
  const passwordsMismatch = newPw && confirmPw && newPw !== confirmPw;

  return (
    <div className="animate-fade-in" style={{ maxWidth: 560, margin: "0 auto" }}>
      {/* Page header */}
      <div className="animate-slide-down" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
              flexShrink: 0,
            }}
          >
            <Lock size={18} color="#fff" strokeWidth={2} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.25rem",
                fontWeight: 800,
                margin: 0,
                background: "linear-gradient(135deg,#6366f1,#8b5cf6,#06b6d4)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {changePassword}
            </h1>
            <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)" }}>
              Keep your account secure with a strong password
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 20, gridTemplateColumns: "1fr" }}>
        {/* ── Form card ── */}
        <div
          className="glass animate-slide-up"
          style={{ borderRadius: 20, padding: "28px 28px 24px" }}
        >
          {/* Shimmer */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: "-60%",
              width: "40%",
              height: "100%",
              background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.03),transparent)",
              animation: "beam 6s ease-in-out infinite",
              pointerEvents: "none",
              borderRadius: 20,
              overflow: "hidden",
            }}
          />

          <form onSubmit={formik.handleSubmit} noValidate>
            {/* Current password */}
            <div style={{ marginBottom: 4 }}>
              <BaseInput
                name="current_password"
                type="password"
                label={currentPassword}
                placeholder={PlaceHolderFormat(currentPassword)}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                value={formik.values.current_password}
                touched={formik.touched.current_password}
                error={formik.errors.current_password}
              />
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: "var(--glass-border)", margin: "4px 0 16px" }} />

            {/* New password */}
            <BaseInput
              name="new_password"
              type="password"
              label={newPassword}
              placeholder={PlaceHolderFormat(newPassword)}
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              value={formik.values.new_password}
              touched={formik.touched.new_password}
              error={formik.errors.new_password}
            />
            <StrengthBar password={newPw} />

            {/* Confirm password */}
            <BaseInput
              name="confirm_password"
              type="password"
              label={ConfirmPassword}
              placeholder={PlaceHolderFormat(ConfirmPassword)}
              handleChange={formik.handleChange}
              handleBlur={formik.handleBlur}
              value={formik.values.confirm_password}
              touched={formik.touched.confirm_password}
              error={formik.errors.confirm_password}
            />

            {/* Match indicator */}
            {(passwordsMatch || passwordsMismatch) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: -8,
                  marginBottom: 16,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: passwordsMatch ? "#10b981" : "#f43f5e",
                  animation: "fadeIn 0.2s ease",
                }}
              >
                {passwordsMatch ? (
                  <CheckCircle size={13} strokeWidth={2.5} />
                ) : (
                  <AlertCircle size={13} strokeWidth={2.5} />
                )}
                <span>{passwordsMatch ? "Passwords match" : "Passwords don't match"}</span>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
              <BaseButton
                type="submit"
                color="primary"
                loader={loader}
                disabled={loader}
                style={{ padding: "10px 24px", fontSize: "0.875rem" }}
              >
                {loader ? Loading : Submit}
              </BaseButton>
              <BaseButton
                type="button"
                color="cancel"
                onClick={() => navigate(-1)}
                style={{ padding: "8px 16px" }}
              >
                Cancel
              </BaseButton>
            </div>
          </form>
        </div>

        {/* ── Security tips card ── */}
        <div
          className="glass animate-slide-up"
          style={{
            borderRadius: 16,
            padding: "18px 20px",
            animationDelay: "80ms",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Shield
              size={15}
              strokeWidth={2}
              style={{ color: "var(--brand-indigo)", flexShrink: 0 }}
            />
            <span style={{ fontSize: "0.8125rem", fontWeight: 700, color: "var(--text-primary)" }}>
              Password Tips
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {tips.map((tip, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                  }}
                />
                <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
